"use client";

import { Button } from "@/components/ui/button";
import { type Framework, getAllFrameworks } from "@/content/guides";
import { ArrowRight, Search } from "lucide-react";
import Link from "next/link";
import { useState } from "react";

type LanguageGroup = {
	id: string;
	label: string;
	icon: string;
	frameworks: Framework[];
};

function groupFrameworksByLanguage(frameworks: Framework[]): LanguageGroup[] {
	const jsFrameworks = [
		"nextjs",
		"remix",
		"astro",
		"sveltekit",
		"nuxt",
		"hono",
		"fastify",
		"koa",
		"deno",
		"bun",
		"elysia",
	];
	const pyFrameworks = ["django", "flask", "fastapi"];
	const goFrameworks = ["gin", "echo", "fiber"];
	const phpFrameworks = ["laravel", "symfony"];

	return [
		{
			id: "javascript",
			label: "JavaScript / TypeScript",
			icon: "JS",
			frameworks: frameworks.filter((f) => jsFrameworks.includes(f.slug)),
		},
		{
			id: "python",
			label: "Python",
			icon: "PY",
			frameworks: frameworks.filter((f) => pyFrameworks.includes(f.slug)),
		},
		{
			id: "go",
			label: "Go",
			icon: "GO",
			frameworks: frameworks.filter((f) => goFrameworks.includes(f.slug)),
		},
		{
			id: "php",
			label: "PHP",
			icon: "PHP",
			frameworks: frameworks.filter((f) => phpFrameworks.includes(f.slug)),
		},
	];
}

function FrameworkCard({ framework }: { framework: Framework }) {
	return (
		<Link
			href={`/guides/${framework.slug}`}
			className="group block p-5 bg-white rounded-xl border border-gray-100 hover:border-brand-blue/30 hover:shadow-lg transition-all"
		>
			<div className="flex items-start justify-between">
				<div>
					<h3 className="font-display text-lg font-bold text-gray-900 group-hover:text-brand-blue transition">
						{framework.name}
					</h3>
					<p className="text-gray-600 text-sm mt-1 line-clamp-2">
						{framework.description}
					</p>
				</div>
				<ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-brand-blue group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
			</div>
			<div className="flex flex-wrap gap-1.5 mt-3">
				{framework.features.slice(0, 2).map((feature) => (
					<span
						key={feature}
						className="text-xs px-2 py-1 bg-gray-50 text-gray-500 rounded-md"
					>
						{feature}
					</span>
				))}
			</div>
		</Link>
	);
}

export function FrameworkGuides() {
	const frameworks = getAllFrameworks();
	const groups = groupFrameworksByLanguage(frameworks);

	const [activeLanguage, setActiveLanguage] = useState("javascript");
	const [searchQuery, setSearchQuery] = useState("");

	const activeGroup = groups.find((g) => g.id === activeLanguage);
	const filteredFrameworks = searchQuery
		? frameworks.filter(
				(f) =>
					f.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
					f.slug.toLowerCase().includes(searchQuery.toLowerCase()),
			)
		: activeGroup?.frameworks || [];

	return (
		<div>
			<div className="mb-6">
				<h2 className="font-display text-xl font-bold text-gray-900">
					Framework integration guides
				</h2>
				<p className="text-gray-600 text-sm mt-1">
					Step-by-step setup for your stack.
				</p>
			</div>

			{/* Search and Language Tabs */}
			<div className="sticky top-16 z-20 bg-gradient-to-b from-gray-50/50 via-gray-50/50 to-transparent backdrop-blur-sm pb-4 pt-2 -mx-6 px-6">
				{/* Search */}
				<div className="relative mb-4">
					<Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
					<input
						type="text"
						placeholder="Search frameworks..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 bg-white text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-all"
					/>
					{searchQuery && (
						<button
							type="button"
							onClick={() => setSearchQuery("")}
							className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
						>
							Clear
						</button>
					)}
				</div>

				{/* Language Tabs */}
				{!searchQuery && (
					<div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
						{groups.map((group) => (
							<button
								key={group.id}
								type="button"
								onClick={() => setActiveLanguage(group.id)}
								className={`
									flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all
									${
										activeLanguage === group.id
											? "bg-brand-blue text-white shadow-md shadow-brand-blue/20"
											: "bg-gray-100 text-gray-600 hover:bg-gray-200"
									}
								`}
							>
								<span
									className={`
									text-xs font-bold px-1.5 py-0.5 rounded
									${activeLanguage === group.id ? "bg-white/20" : "bg-gray-200"}
								`}
								>
									{group.icon}
								</span>
								{group.label}
								<span
									className={`text-xs ${activeLanguage === group.id ? "text-white/70" : "text-gray-400"}`}
								>
									{group.frameworks.length}
								</span>
							</button>
						))}
					</div>
				)}
			</div>

			{/* Search Results Count */}
			{searchQuery && (
				<div className="mb-4 text-sm text-gray-500">
					Found {filteredFrameworks.length} framework
					{filteredFrameworks.length !== 1 ? "s" : ""} matching "{searchQuery}"
				</div>
			)}

			{/* Framework Cards Grid */}
			<div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
				{filteredFrameworks.length > 0 ? (
					filteredFrameworks.map((framework) => (
						<FrameworkCard key={framework.slug} framework={framework} />
					))
				) : (
					<div className="col-span-full text-center py-12 text-gray-500">
						No frameworks found matching your search.
					</div>
				)}
			</div>

			{/* CTA */}
			<div className="mt-12 bg-gradient-to-br from-brand-light/50 to-brand-light/30 rounded-2xl p-8 text-center border border-brand-light">
				<h2 className="font-display text-2xl font-bold text-gray-900 mb-2">
					Ready to send emails?
				</h2>
				<p className="text-gray-600 mb-6">
					Start free with 3,000 emails/month. No credit card required.
				</p>
				<Button asChild size="lg">
					<Link href="/signup">Get Started Free</Link>
				</Button>
			</div>
		</div>
	);
}
